import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Skeleton,
  Chip,
} from "@mui/material";
import {
  ContentCopy,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Lock,
  Person,
  Fingerprint,
  Shield,
  WarningAmber,
  Key,
  VerifiedUser,
  FileDownload,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../../auth/useAuth";

import { APP_API_BASE } from "../../../config/api";
const API_BASE =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") ||
  `${APP_API_BASE}`;

function useCopy() {
  const [copiedKey, setCopiedKey] = useState(null);
  const copy = (key, value) => {
    navigator.clipboard.writeText(value).catch(() => { });
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };
  return { copiedKey, copy };
}

function CredRow({
  icon,
  label,
  value,
  fieldKey,
  copiedKey,
  onCopy,
  masked,
  children,
  alwaysShowCopy = false,
}) {
  const isCopied = copiedKey === fieldKey;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: "14px 16px",
        borderRadius: "12px",
        border: "1.5px solid",
        borderColor: isCopied ? "#bbf7d0" : "#F1F5F9",
        background: isCopied
          ? "linear-gradient(135deg,#f0fdf4,#dcfce7)"
          : "#FAFBFF",
        transition: "all 0.22s ease",
        "&:hover": {
          borderColor: "#BFDBFE",
          background: "#EFF6FF",
          "& .copy-btn": { opacity: 1 },
        },
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          background: isCopied
            ? "linear-gradient(135deg,#22c55e,#16a34a)"
            : "linear-gradient(135deg,#1976d2,#42a5f5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "all 0.22s",
          boxShadow: isCopied
            ? "0 3px 10px rgba(34,197,94,0.3)"
            : "0 3px 10px rgba(25,118,210,0.25)",
        }}
      >
        {isCopied ? (
          <CheckCircle sx={{ fontSize: 18, color: "#fff" }} />
        ) : (
          <Box sx={{ color: "#fff", display: "flex" }}>{icon}</Box>
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "10px",
            fontWeight: 700,
            color: isCopied ? "#16a34a" : "#94A3B8",
            textTransform: "uppercase",
            letterSpacing: "0.6px",
            mb: 0.3,
            transition: "color 0.2s",
          }}
        >
          {isCopied ? "Copied!" : label}
        </Typography>
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: "#0F172A",
            fontFamily: "monospace",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            letterSpacing: masked ? "3px" : "0.2px",
          }}
        >
          {value}
        </Typography>
      </Box>

      <Box
        sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}
      >
        {children}
        <Tooltip title={isCopied ? "Copied!" : `Copy ${label}`} placement="top">
          <IconButton
            className="copy-btn"
            size="small"
            onClick={() => onCopy(fieldKey, value)}
            sx={{
              opacity: alwaysShowCopy ? 1 : 0,
              width: 32,
              height: 32,
              borderRadius: "8px",
              background: isCopied ? "#dcfce7" : "#EFF6FF",
              border: "1px solid",
              borderColor: isCopied ? "#bbf7d0" : "#BFDBFE",
              transition: "all 0.18s",
              "&:hover": { background: isCopied ? "#bbf7d0" : "#DBEAFE" },
            }}
          >
            <ContentCopy
              sx={{ fontSize: 14, color: isCopied ? "#16a34a" : "#1976d2" }}
            />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function DeviceCredentialsCard({
  deviceId,
  credentialRefreshKey = 0,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: "" });
  const { activeOrg } = useAuth();
  const { copiedKey, copy } = useCopy();

  const PASSWORD_VIEW_KEY = `device_password_viewed_${deviceId}`;
  const [hasViewedPassword, setHasViewedPassword] = useState(
    () => sessionStorage.getItem(PASSWORD_VIEW_KEY) === "true",
  );

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API_BASE}/api/v1/devices/${deviceId}/connection`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        },
      })
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, [deviceId, activeOrg?.id, credentialRefreshKey]);

  useEffect(() => {
    if (!showPassword) return;
    const t = setTimeout(() => setShowPassword(false), 10000);
    return () => clearTimeout(t);
  }, [showPassword]);

  useEffect(() => {
    setHasViewedPassword(sessionStorage.getItem(PASSWORD_VIEW_KEY) === "true");
  }, [PASSWORD_VIEW_KEY, credentialRefreshKey]);

  const isTls =
    String(data?.security_mode || "PLAINTEXT").toUpperCase() === "TLS";

  const handleViewPassword = () => {
    if (hasViewedPassword || !data?.password) return;
    setShowPassword(true);
    setHasViewedPassword(true);
    sessionStorage.setItem(PASSWORD_VIEW_KEY, "true");
    setSnackbar({ open: true, msg: "Password visible for 10 seconds only" });
    setTimeout(() => setShowPassword(false), 10000);
  };

  const handleCopy = (key, value) => {
    copy(key, value);
    setSnackbar({
      open: true,
      msg: `${key === "device_id" ? "Device ID" : "Value"} copied to clipboard`,
    });
  };

  const fetchTlsFileText = async (url) => {
    const token = localStorage.getItem("authToken");
    if (!token || !url) return null;

    const response = await axios.get(`${API_BASE}${url}`, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
        ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
      },
    });

    return response.data.text();
  };

  const handleDownload = async (url, filename) => {
    const token = localStorage.getItem("authToken");
    if (!token || !url) return;

    try {
      const response = await axios.get(`${API_BASE}${url}`, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        },
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      setSnackbar({ open: true, msg: `${filename} downloaded` });
    } catch (error) {
      setSnackbar({ open: true, msg: `Unable to download ${filename}` });
    }
  };

  const handleTlsCopy = async (key, fallbackValue, url, successLabel) => {
    if (!url) {
      handleCopy(key, fallbackValue);
      return;
    }

    try {
      const text = await fetchTlsFileText(url);
      if (!text) {
        throw new Error("empty TLS file");
      }
      copy(key, text);
      setSnackbar({ open: true, msg: `${successLabel} copied to clipboard` });
    } catch (error) {
      setSnackbar({ open: true, msg: `Unable to copy ${successLabel}` });
    }
  };

  return (
    <>
      <Box
        sx={{
          borderRadius: "18px",
          overflow: "hidden",
          border: "1.5px solid #E2E8F0",
          background: "#fff",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "linear-gradient(135deg, #EFF6FF 0%, #F0F9FF 100%)",
            borderBottom: "1.5px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #1976d2, #42a5f5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(25,118,210,0.3)",
              }}
            >
              <Shield sx={{ color: "#fff", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 700,
                  color: "#0F172A",
                  lineHeight: 1.2,
                }}
              >
                Device Credentials
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "#64748B", mt: 0.2 }}>
                {isTls
                  ? "Certificate-backed MQTTS identity"
                  : "Used for MQTT authentication"}
              </Typography>
            </Box>
          </Box>

          <Chip
            icon={<CheckCircle sx={{ fontSize: "13px !important" }} />}
            label={isTls ? "TLS Enabled" : "Plain MQTT"}
            size="small"
            sx={{
              background: isTls ? "#DCFCE7" : "#DBEAFE",
              color: isTls ? "#15803D" : "#1D4ED8",
              fontWeight: 700,
              fontSize: "11px",
              height: 24,
              border: `1px solid ${isTls ? "#BBF7D0" : "#BFDBFE"}`,
              "& .MuiChip-icon": { color: isTls ? "#16a34a" : "#2563EB" },
            }}
          />
        </Box>

        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {loading ? (
            [1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  p: "14px 16px",
                  borderRadius: "12px",
                  border: "1.5px solid #F1F5F9",
                  background: "#FAFBFF",
                }}
              >
                <Skeleton width={80} height={12} sx={{ mb: 0.5 }} />
                <Skeleton width="70%" height={18} />
              </Box>
            ))
          ) : (
            <>
              <CredRow
                icon={<Fingerprint sx={{ fontSize: 18 }} />}
                label="Device ID"
                value={data?.device_id || "—"}
                fieldKey="device_id"
                copiedKey={copiedKey}
                onCopy={handleCopy}
              />

              <CredRow
                icon={<Person sx={{ fontSize: 18 }} />}
                label={isTls ? "Username / Identity" : "Username"}
                value={data?.username || "—"}
                fieldKey="username"
                copiedKey={copiedKey}
                onCopy={handleCopy}
              />

              {isTls ? (
                <>
                  <CredRow
                    icon={<VerifiedUser sx={{ fontSize: 18 }} />}
                    label="Certificate Identity"
                    value={data?.client_cert_identity || data?.username || "—"}
                    fieldKey="client_cert_identity"
                    copiedKey={copiedKey}
                    onCopy={handleCopy}
                  />
                  <CredRow
                    icon={<Shield sx={{ fontSize: 18 }} />}
                    label="CA Certificate"
                    value={data?.ca_cert_filename || "ca.crt"}
                    fieldKey="ca_cert_filename"
                    copiedKey={copiedKey}
                    onCopy={() =>
                      handleTlsCopy(
                        "ca_cert_filename",
                        data?.ca_cert_filename || "ca.crt",
                        data?.ca_cert_download_url,
                        "CA certificate",
                      )
                    }
                    alwaysShowCopy
                    children={
                      data?.ca_cert_download_url ? (
                        <Tooltip title="Download CA certificate" placement="top">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDownload(
                                data.ca_cert_download_url,
                                data?.ca_cert_filename || "ca.crt",
                              )
                            }
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "8px",
                              background: "#EFF6FF",
                              border: "1px solid #BFDBFE",
                              "&:hover": { background: "#DBEAFE" },
                            }}
                          >
                            <FileDownload
                              sx={{ fontSize: 15, color: "#1976d2" }}
                            />
                          </IconButton>
                        </Tooltip>
                      ) : null
                    }
                  />
                  <CredRow
                    icon={<VerifiedUser sx={{ fontSize: 18 }} />}
                    label="Device Certificate"
                    value={data?.client_cert_filename || "device.crt"}
                    fieldKey="client_cert_filename"
                    copiedKey={copiedKey}
                    onCopy={() =>
                      handleTlsCopy(
                        "client_cert_filename",
                        data?.client_cert_filename || "device.crt",
                        data?.client_cert_download_url,
                        "device certificate",
                      )
                    }
                    alwaysShowCopy
                    children={
                      data?.client_cert_download_url ? (
                        <Tooltip title="Download device certificate" placement="top">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDownload(
                                data.client_cert_download_url,
                                data?.client_cert_filename || "device.crt",
                              )
                            }
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "8px",
                              background: "#EFF6FF",
                              border: "1px solid #BFDBFE",
                              "&:hover": { background: "#DBEAFE" },
                            }}
                          >
                            <FileDownload
                              sx={{ fontSize: 15, color: "#1976d2" }}
                            />
                          </IconButton>
                        </Tooltip>
                      ) : null
                    }
                  />
                  <CredRow
                    icon={<Key sx={{ fontSize: 18 }} />}
                    label="Device Key"
                    value={data?.client_key_filename || "device.key"}
                    fieldKey="client_key_filename"
                    copiedKey={copiedKey}
                    onCopy={() =>
                      handleTlsCopy(
                        "client_key_filename",
                        data?.client_key_filename || "device.key",
                        data?.client_key_download_url,
                        "device key",
                      )
                    }
                    alwaysShowCopy
                    children={
                      data?.client_key_download_url ? (
                        <Tooltip title="Download device key" placement="top">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDownload(
                                data.client_key_download_url,
                                data?.client_key_filename || "device.key",
                              )
                            }
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "8px",
                              background: "#EFF6FF",
                              border: "1px solid #BFDBFE",
                              "&:hover": { background: "#DBEAFE" },
                            }}
                          >
                            <FileDownload
                              sx={{ fontSize: 15, color: "#1976d2" }}
                            />
                          </IconButton>
                        </Tooltip>
                      ) : null
                    }
                  />
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: "14px 16px",
                    borderRadius: "12px",
                    border: "1.5px solid",
                    borderColor: hasViewedPassword ? "#FEF3C7" : "#F1F5F9",
                    background: hasViewedPassword ? "#FFFBEB" : "#FAFBFF",
                    transition: "all 0.22s ease",
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      background: hasViewedPassword
                        ? "linear-gradient(135deg,#f59e0b,#fbbf24)"
                        : "linear-gradient(135deg,#1976d2,#42a5f5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: hasViewedPassword
                        ? "0 3px 10px rgba(245,158,11,0.3)"
                        : "0 3px 10px rgba(25,118,210,0.25)",
                    }}
                  >
                    <Lock sx={{ fontSize: 18, color: "#fff" }} />
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      width: "100%",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: {
                          xs: "9px",
                          sm: "10px",
                          md: "10px",
                        },
                        fontWeight: 700,
                        color: hasViewedPassword ? "#B45309" : "#94A3B8",
                        textTransform: "uppercase",
                        letterSpacing: {
                          xs: "0.4px",
                          sm: "0.6px",
                        },
                        mb: 0.5,
                        lineHeight: 1.2,
                        wordBreak: "break-word",
                      }}
                    >
                      Password
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: {
                          xs: "11px",
                          sm: "12px",
                          md: "13px",
                        },
                        fontWeight: 500,
                        color: "#0F172A",
                        fontFamily: "monospace",
                        letterSpacing: showPassword
                          ? {
                            xs: "0px",
                            sm: "0.1px",
                            md: "0.2px",
                          }
                          : {
                            xs: "2px",
                            sm: "3px",
                            md: "4px",
                          },
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "100%",
                      }}
                    >
                      {showPassword && data?.password
                        ? data.password
                        : "••••••••••••••••"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    {hasViewedPassword && (
                      <Tooltip
                        title="Password already viewed. Regenerate credentials to view again."
                        placement="top"
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <WarningAmber
                            sx={{ fontSize: 16, color: "#F59E0B" }}
                          />
                        </Box>
                      </Tooltip>
                    )}
                    <Tooltip
                      title={
                        hasViewedPassword
                          ? "Already viewed — regenerate to reveal again"
                          : "View password once (hidden after 10s)"
                      }
                      placement="top"
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleViewPassword}
                          disabled={hasViewedPassword || !data?.password}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                            background: hasViewedPassword
                              ? "#FEF3C7"
                              : "#EFF6FF",
                            border: "1px solid",
                            borderColor: hasViewedPassword
                              ? "#FDE68A"
                              : "#BFDBFE",
                            "&:hover": {
                              background: hasViewedPassword
                                ? "#FDE68A"
                                : "#DBEAFE",
                            },
                            "&.Mui-disabled": {
                              opacity: 0.5,
                            },
                          }}
                        >
                          {showPassword ? (
                            <VisibilityOff
                              sx={{ fontSize: 14, color: "#F59E0B" }}
                            />
                          ) : (
                            <Visibility
                              sx={{
                                fontSize: 14,
                                color: hasViewedPassword
                                  ? "#F59E0B"
                                  : "#1976d2",
                              }}
                            />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>

        {!loading && (
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderTop: "1.5px solid #F1F5F9",
              background: "#FAFBFF",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {isTls ? (
              <>
                <Shield sx={{ fontSize: 13, color: "#0EA5E9" }} />
                <Typography sx={{ fontSize: "11px", color: "#0369A1" }}>
                  TLS devices use per-device certificate files generated on the
                  broker side. Use the connection profile to retrieve the
                  matching `ca.crt`, `device.crt`, and `device.key`.
                </Typography>
              </>
            ) : hasViewedPassword ? (
              <>
                <WarningAmber sx={{ fontSize: 13, color: "#F59E0B" }} />
                <Typography
                  sx={{ fontSize: "11px", color: "#B45309", fontWeight: 500 }}
                >
                  Password already viewed this session. Regenerate credentials
                  to reveal again.
                </Typography>
              </>
            ) : (
              <>
                <Shield sx={{ fontSize: 13, color: "#94A3B8" }} />
                <Typography sx={{ fontSize: "11px", color: "#94A3B8" }}>
                  Password is hidden by default. Viewable once per session for
                  security.
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{ borderRadius: "10px", fontSize: "12px", fontWeight: 600 }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
